import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DatePipe } from '@angular/common';

import { KsFirstLook } from '../../core/kickstart/kickstart.types';
import { ZarPipe } from '../pipes/zar.pipe';

/**
 * Legal's standing line, final (DOC-78-A §1, `LEG-006 §1`). Not UI's to vary
 * and not ours: a build that words it differently is a question for Legal.
 */
export const FIRST_LOOK_STANDING_LINE =
  "This is a first look at what CapX may be able to fund, based only on the bank statements you've given. " +
  'It is provisional. It is not an offer, and CapX is not bound by it. ' +
  'After a person at CapX has reviewed the application, the amount may be lower, ' +
  'or CapX may not be able to offer funding. ' +
  'Any offer will come to you separately, with its terms.';

/**
 * Whether the page carries the first look at all: only while the application
 * is with CapX. A draft has nothing to look at, and a declined file is not
 * "being processed".
 */
export function firstLookApplies(applicationStage: string): boolean {
  return applicationStage === 'SUBMITTED' || applicationStage === 'MORE_INFO_REQUIRED';
}

/**
 * The first look on a Kickstart application's page (FLOW-224, DOC-78) — the
 * same for the agent and the client (UI-DA-78-01).
 *
 * 🚨 The screen works nothing out (UI-DA-78-05): it never compares the figure
 * with the floor, the ceiling or the amount applied for. `firstLook` is the
 * back end's decision; absent or null, the page shows only the processing line
 * — no amount, no placeholder, no reserved space, never why (UI-DA-78-04).
 * Nothing here may read as an offer: no control, price, term or success styling
 * (UI-DA-78-03). An issued offer takes the first look's place.
 */
@Component({
  selector: 'app-first-look',
  imports: [DatePipe, ZarPipe],
  templateUrl: './first-look.html',
  styleUrl: './first-look.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirstLookComponent {
  readonly firstLook = input<KsFirstLook | null | undefined>(null);
  readonly amountRequested = input.required<string>();
  readonly offerIssued = input(false);

  protected readonly standingLine = FIRST_LOOK_STANDING_LINE;

  protected readonly monthsLabel = computed(() => {
    const n = this.firstLook()?.bank_statement_months ?? 0;
    return n === 1 ? '1 month' : `${n} months`;
  });
}
